// redux/ugc/reactionsSlice.ts

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import type { AxiosError } from "axios";
import { reactionsApi } from "../../services/ugc/reactionsApi";
import type {
  ReactionDTO,
  ReactionType,
  ReactionsSummaryItem,
} from "../../services/ugc/reactionsApi";

type Status = "idle" | "loading" | "succeeded" | "failed";
type RejectedPayload = { status?: number; data?: any };

// key per object
export const keyFor = (content_type: string, object_id: number) =>
  `${content_type}:${object_id}`;

// ---- Thunks ----
export const fetchReactions = createAsyncThunk<
  { key: string; items: ReactionDTO[] },
  { content_type: string; object_id: number },
  { rejectValue: RejectedPayload }
>("reactions/fetchReactions", async ({ content_type, object_id }, { rejectWithValue }) => {
  try {
    const items = await reactionsApi.listByObject(content_type, object_id);
    return { key: keyFor(content_type, object_id), items };
  } catch (err) {
    const e = err as AxiosError;
    return rejectWithValue({ status: e.response?.status, data: e.response?.data });
  }
});

export const fetchReactionsSummary = createAsyncThunk<
  { key: string; items: ReactionsSummaryItem[] },
  { content_type: string; object_id: number },
  { rejectValue: RejectedPayload }
>(
  "reactions/fetchReactionsSummary",
  async ({ content_type, object_id }, { rejectWithValue }) => {
    try {
      const items = await reactionsApi.summaryByObject(content_type, object_id);
      return { key: keyFor(content_type, object_id), items };
    } catch (err) {
      const e = err as AxiosError;
      return rejectWithValue({ status: e.response?.status, data: e.response?.data });
    }
  }
);

export const fetchReactionsWithMessage = createAsyncThunk<
  { key: string; items: ReactionDTO[] },
  { content_type: string; object_id: number; reaction_type?: ReactionType },
  { rejectValue: RejectedPayload }
>(
  "reactions/fetchReactionsWithMessage",
  async ({ content_type, object_id, reaction_type }, { rejectWithValue }) => {
    try {
      const items = await reactionsApi.listWithMessage(content_type, object_id, reaction_type);
      return { key: `${keyFor(content_type, object_id)}:msg`, items };
    } catch (err) {
      const e = err as AxiosError;
      return rejectWithValue({ status: e.response?.status, data: e.response?.data });
    }
  }
);

export const sendReaction = createAsyncThunk<
  { key: string; status: number; data?: ReactionDTO },
  {
    content_type: string;
    object_id: number;
    reaction_type: ReactionType;
    message?: string | null;
  },
  { rejectValue: RejectedPayload }
>("reactions/sendReaction", async (args, { rejectWithValue, dispatch }) => {
  try {
    const res = await reactionsApi.reactToObject(args);
    const key = keyFor(args.content_type, args.object_id);

    // Keep current API-based refresh behavior
    dispatch(fetchReactions({ content_type: args.content_type, object_id: args.object_id }));
    dispatch(fetchReactionsSummary({ content_type: args.content_type, object_id: args.object_id }));

    return { key, status: res.status, data: res.data };
  } catch (err) {
    const e = err as AxiosError;
    return rejectWithValue({ status: e.response?.status, data: e.response?.data });
  }
});

// ---- State ----
interface ReactionsState {
  itemsByKey: Record<string, ReactionDTO[] | undefined>;
  summaryByKey: Record<string, ReactionsSummaryItem[] | undefined>;
  listStatusByKey: Record<string, Status>;
  summaryStatusByKey: Record<string, Status>;
  opStatusByKey: Record<string, Status>;
  error?: string | null;
}

const initialState: ReactionsState = {
  itemsByKey: {},
  summaryByKey: {},
  listStatusByKey: {},
  summaryStatusByKey: {},
  opStatusByKey: {},
  error: null,
};

// ---- Helpers ----
const extractApiError = (action: any): string => {
  const p = action.payload as RejectedPayload | undefined;
  if (p?.data) {
    if (typeof p.data === "string") return p.data;
    if (typeof (p.data as any).detail === "string") return (p.data as any).detail;
    if (typeof (p.data as any).message === "string") return (p.data as any).message;
    try {
      return JSON.stringify(p.data);
    } catch {}
  }
  return action.error?.message ?? "Request failed";
};

const normalizeSummaryItems = (items: any[]): ReactionsSummaryItem[] => {
  return items
    .map((item) => ({
      reaction_type: item?.reaction_type as ReactionType,
      count: Number(item?.count ?? 0),
    }))
    .filter(
      (item) =>
        !!item.reaction_type &&
        Number.isFinite(item.count) &&
        item.count >= 0,
    );
};

// ---- Slice ----
const reactionsSlice = createSlice({
  name: "reactions",
  initialState,
  reducers: {
    clearReactionsForKey(state, action: PayloadAction<string>) {
      const key = action.payload;
      delete state.itemsByKey[key];
      delete state.summaryByKey[key];
      delete state.listStatusByKey[key];
      delete state.summaryStatusByKey[key];
      delete state.opStatusByKey[key];
    },

    clearReactionsError(state) {
      state.error = null;
    },

    // Realtime: authoritative summary sync for one object
    applyRealtimeSummary(
      state,
      action: PayloadAction<{
        key: string;
        items: ReactionsSummaryItem[];
      }>,
    ) {
      const { key, items } = action.payload;
      state.summaryByKey[key] = normalizeSummaryItems(items as any[]);
      state.summaryStatusByKey[key] = "succeeded";
    },

    // Realtime: authoritative inbox/message list sync for owner
    applyRealtimeInbox(
      state,
      action: PayloadAction<{
        key: string;
        items: ReactionDTO[];
      }>,
    ) {
      const { key, items } = action.payload;
      state.itemsByKey[key] = items;
      state.listStatusByKey[key] = "succeeded";
    },

    setReactionsRealtimeError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      // List all
      .addCase(fetchReactions.pending, (state, action) => {
        const { content_type, object_id } = action.meta.arg;
        state.listStatusByKey[keyFor(content_type, object_id)] = "loading";
      })
      .addCase(fetchReactions.fulfilled, (state, action) => {
        const { key, items } = action.payload;
        state.itemsByKey[key] = items;
        state.listStatusByKey[key] = "succeeded";
      })
      .addCase(fetchReactions.rejected, (state, action) => {
        const { content_type, object_id } = action.meta.arg;
        state.listStatusByKey[keyFor(content_type, object_id)] = "failed";
        state.error = extractApiError(action);
      })

      // Summary
      .addCase(fetchReactionsSummary.pending, (state, action) => {
        const { content_type, object_id } = action.meta.arg;
        state.summaryStatusByKey[keyFor(content_type, object_id)] = "loading";
      })
      .addCase(fetchReactionsSummary.fulfilled, (state, action) => {
        const { key, items } = action.payload;
        state.summaryByKey[key] = items;
        state.summaryStatusByKey[key] = "succeeded";
      })
      .addCase(fetchReactionsSummary.rejected, (state, action) => {
        const { content_type, object_id } = action.meta.arg;
        state.summaryStatusByKey[keyFor(content_type, object_id)] = "failed";
        state.error = extractApiError(action);
      })

      // With message only
      .addCase(fetchReactionsWithMessage.pending, (state, action) => {
        const { content_type, object_id } = action.meta.arg;
        state.listStatusByKey[`${keyFor(content_type, object_id)}:msg`] = "loading";
      })
      .addCase(fetchReactionsWithMessage.fulfilled, (state, action) => {
        const { key, items } = action.payload;
        state.itemsByKey[key] = items;
        state.listStatusByKey[key] = "succeeded";
      })
      .addCase(fetchReactionsWithMessage.rejected, (state, action) => {
        const { content_type, object_id } = action.meta.arg;
        state.listStatusByKey[`${keyFor(content_type, object_id)}:msg`] = "failed";
        state.error = extractApiError(action);
      })

      // Send / toggle
      .addCase(sendReaction.pending, (state, action) => {
        const { content_type, object_id } = action.meta.arg;
        state.opStatusByKey[keyFor(content_type, object_id)] = "loading";
      })
      .addCase(sendReaction.fulfilled, (state, action) => {
        const { key } = action.payload;
        state.opStatusByKey[key] = "succeeded";
      })
      .addCase(sendReaction.rejected, (state, action) => {
        const { content_type, object_id } = action.meta.arg;
        state.opStatusByKey[keyFor(content_type, object_id)] = "failed";
        state.error = extractApiError(action);
      });
  },
});

export const {
  clearReactionsForKey,
  clearReactionsError,
  applyRealtimeSummary,
  applyRealtimeInbox,
  setReactionsRealtimeError,
} = reactionsSlice.actions;

export default reactionsSlice.reducer;